import type { Resultado, Visita } from '@sheafyield/dominio';
import type { Banco } from '@sheafyield/sync';
import { useId } from 'react';

import { nomesDasVisitas, visitasDoDia } from '../dados/consultas.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { Linha, type PropsLinha } from '../ds/Linha.js';
import { Vazio } from '../ds/Vazio.js';
import { horaLocal } from './relogio.js';

/** Os seis resultados da spec §8 em português de gente. É daqui que saem
 *  também os rótulos dos chips da folha Registrar: dois mapas com os mesmos
 *  seis textos se desencontram na primeira correção de redação.
 *
 *  Dois rótulos são mais curtos do que a spec escreveu, e a razão é medida:
 *  em AX5 (raiz de 53 px numa tela de 375 px) cabem ~11 caracteres por linha
 *  dentro de um chip, e "Não interessado" (15) e "Deixei publicação" (17)
 *  ocupavam duas linhas cada um — 160 px de altura em vez de 91, os dois
 *  sozinhos comendo um chip inteiro de espaço. "Sem interesse" e
 *  "Publicação" dizem o mesmo em uma linha. O texto é o do usuário: se ele
 *  preferir os originais, é escolha dele, e o custo é um chip a menos à
 *  vista. */
export const ROTULO_DO_RESULTADO: Record<Resultado, string> = {
  nao_estava: 'Não estava',
  conversamos: 'Conversamos',
  nao_interessado: 'Sem interesse',
  deixei_publicacao: 'Publicação',
  revisita: 'Revisita',
  estudo: 'Estudo',
};

export interface DepsDasVisitas {
  banco: Banco;
  motor: MotorObservavel;
  /** O dia local corrente, em ISO com `Z` — o `de` de `limitesDoDia`. Vem
   *  de fora, e não de um `useDiaLocal()` daqui, porque a tela Hoje tem três
   *  blocos que recortam o mesmo dia: três chamadas independentes eram três
   *  temporizadores até a meia-noite e três ouvintes de `visibilitychange`
   *  para o mesmo valor. Quem o mantém é a `HojeTelaCom`. */
  dia: string;
}

/**
 * Uma visita virando linha: o que aconteceu, o que se anotou e a que horas.
 *
 * Com ficha viva, o nome é o título e o resultado desce para o detalhe. Se a
 * ficha virou lápide (ou ainda não chegou ao aparelho), o título preserva que
 * houve alguém: "Pessoa apagada". Só `pessoa_id` nulo é a visita anônima do
 * caminho curto, que continua usando o resultado como título.
 *
 * O detalhe é a nota, e na falta dela a publicação deixada: no caminho curto
 * a publicação vem preenchida com a última usada, então é ela que diferencia
 * uma linha da outra numa manhã de portas.
 */
function propsDaLinha(
  v: Visita,
  nomes: ReadonlyMap<string, string | null>,
): PropsLinha {
  const nota = v.nota.trim();
  const publicacao = v.publicacao.trim();
  const complemento = nota || publicacao;
  const temFicha = nomes.has(v.id);
  const nome = nomes.get(v.id);
  const segundaLinha = temFicha
    ? [ROTULO_DO_RESULTADO[v.resultado], complemento]
        .filter(Boolean)
        .join(' · ')
    : complemento;
  return {
    titulo: temFicha
      ? (nome ?? 'Pessoa apagada')
      : ROTULO_DO_RESULTADO[v.resultado],
    ...(nome === null ? { classeDoTitulo: 'hoje__visita-apagada' } : {}),
    ...(segundaLinha === '' ? {} : { detalhe: segundaLinha }),
    meta: horaLocal(v.em),
  };
}

/**
 * As visitas registradas hoje, da mais antiga à mais nova — a ordem em que
 * aconteceram, que é como se relê a manhã.
 *
 * Este é o estado vazio da tela Hoje, e o único: "Nenhuma visita hoje ainda"
 * é uma frase sobre o dia que começou, não sobre o app estar vazio.
 */
export function VisitasDoDia({ banco, motor, dia }: DepsDasVisitas) {
  const idDoTitulo = useId();
  const { dados: leitura, erro } = useConsultaCom(
    motor,
    `visitas-do-dia-${dia}`,
    async () => {
      const visitas = await visitasDoDia(banco);
      return { visitas, nomes: await nomesDasVisitas(banco, visitas) };
    },
  );

  function corpo() {
    // Erro só aparece quando não há nada lido para mostrar: com a lista na
    // tela, uma falha passageira não pode substituí-la por um recado.
    if (erro !== null && leitura === undefined) {
      return (
        <p className="hoje__erro" role="alert">
          Não deu para ler as visitas de hoje: {erro.message}
        </p>
      );
    }
    // A leitura ainda não voltou. Nada aqui de propósito: pintar o estado
    // vazio antes de saber diria "nenhuma visita" a quem tem cinco.
    if (leitura === undefined) return null;
    if (leitura.visitas.length === 0)
      return (
        <Vazio
          titulo="Nenhuma visita hoje ainda."
          descricao="Quando registrar uma visita, ela aparece aqui."
        />
      );
    return (
      <ul className="hoje__lista">
        {leitura.visitas.map((v) => (
          <Linha key={v.id} {...propsDaLinha(v, leitura.nomes)} />
        ))}
      </ul>
    );
  }

  return (
    <section className="hoje__secao" aria-labelledby={idDoTitulo}>
      <h2 className="hoje__titulo" id={idDoTitulo}>
        Visitas de hoje
      </h2>
      {corpo()}
    </section>
  );
}
